import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Calendar, Maximize2 } from 'lucide-react';
import { CalendarDayTooltip } from '../client/CalendarDayTooltip';
import { useUserTimezone } from '../../hooks/useUserTimezone';
import { getUserToday, isToday } from '../../utils/timezoneHelpers';
import { getStatusColor as getScoreStatusColor, SCORE_COLORS } from '../../../config/scoring';

/**
 * MICRO CALENDAR - Compact calendar widget for Client Summary View
 *
 * A smaller version of HealthCalendarTile designed to fit in the right column (~320px).
 * Same functionality:
 * - Score-based cell coloring (Tier 1/2/3)
 * - Tooltip on hover with full metrics
 * - Month navigation
 *
 * Differences from full calendar:
 * - No toggle switches (inherits settings from localStorage)
 * - Compact layout (rectangular cells, minimal legend)
 * - Collapsible to save space
 */

interface DailyHealthData {
  date: string;
  weight?: number;
  steps: number;
  calories: number;
  sleep: number;
  water: number;
  workouts: number;
  energy_level?: number;
  stress_level?: number;
  notes?: string;
  score?: number;
  status?: string;
  active_goals_snapshot?: string[];
  tierStatus?: 'tier3' | 'tier2' | 'tier1' | 'none';
  softScore?: number | null;
}

interface HealthDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  score: number;
  status: 'excellent' | 'good' | 'fair' | 'atRisk' | 'needsSupport' | 'no-data';
  data?: DailyHealthData;
}

interface MicroCalendarProps {
  clientId: string;
  clientTimezone?: string;
  onExpand?: (month: number, year: number) => void;
  onMonthChange?: (month: number, year: number) => void;
  initialMonth?: number;
  initialYear?: number;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const MicroCalendar: React.FC<MicroCalendarProps> = ({ clientId, clientTimezone, onExpand, onMonthChange, initialMonth, initialYear }) => {
  const { getToday: getCoachToday, isToday: isCoachToday } = useUserTimezone();
  // Use client's timezone for day boundaries when available (coach viewing client data)
  const effectiveTimezone = clientTimezone || undefined;
  const getToday = effectiveTimezone
    ? () => getUserToday(effectiveTimezone)
    : getCoachToday;
  const isTodayForClient = effectiveTimezone
    ? (dateString: string) => isToday(dateString, effectiveTimezone)
    : isCoachToday;
  const today = new Date();

  // Collapse state - persist to localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('microCalendarCollapsed');
    return saved === 'true';
  });

  // Always default to current month — coach view should never inherit stale client-side localStorage
  const [currentMonth, setCurrentMonth] = useState(initialMonth ?? today.getMonth());
  const [currentYear, setCurrentYear] = useState(initialYear ?? today.getFullYear());

  // Sync with external month/year changes (e.g., returning from expanded view)
  useEffect(() => {
    if (initialMonth != null) setCurrentMonth(initialMonth);
  }, [initialMonth]);
  useEffect(() => {
    if (initialYear != null) setCurrentYear(initialYear);
  }, [initialYear]);

  const [healthData, setHealthData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [userGoals, setUserGoals] = useState<any>(null);

  // Inherit toggle settings from main calendar localStorage
  const [showAllMetrics] = useState(() => {
    const stored = localStorage.getItem('coach_calendar_show_all_metrics');
    return stored === 'true';
  });
  const [hideMatchingSoftScore] = useState(() => {
    const stored = localStorage.getItem('coach_calendar_hide_matching_soft_score');
    return stored === 'true';
  });

  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    data: DailyHealthData | null;
    x: number;
    y: number;
  }>({
    visible: false,
    data: null,
    x: 0,
    y: 0
  });
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  // Persist collapse state
  useEffect(() => {
    localStorage.setItem('microCalendarCollapsed', String(isCollapsed));
  }, [isCollapsed]);

  // Fetch health data
  useEffect(() => {
    // AbortController for cleanup - prevents state updates after unmount
    // and handles React 18 StrictMode double-mounting
    const abortController = new AbortController();
    let isMounted = true;

    // Set loading immediately (synchronously) to prevent stale data flash
    setLoading(true);

    const fetchHealthData = async () => {
      const token = localStorage.getItem('auth_token');

      // Fetch calendar data (separate try/catch to prevent goals error from clearing data)
      // API expects 1-indexed months (1=Jan), but JavaScript uses 0-indexed (0=Jan)
      try {
        const response = await fetch(
          `/api/coach/clients/${clientId}/metrics/calendar?month=${currentMonth + 1}&year=${currentYear}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: abortController.signal
          }
        );
        const data = await response.json();
        if (isMounted && data.success) {
          setHealthData(data.data.healthData || []);
        } else if (isMounted) {
          setHealthData([]);
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          return; // Exit early on abort
        }
        console.error('Failed to fetch calendar health data:', error);
        if (isMounted) setHealthData([]);
      }

      // Fetch client goals for tier coloring (separate try/catch)
      try {
        const goalsResponse = await fetch(
          `/api/coach/clients/${clientId}/goals`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: abortController.signal
          }
        );
        const goalsData = await goalsResponse.json();
        if (isMounted && goalsData.success) {
          setUserGoals(goalsData.goals);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch goals (non-critical):', error);
        }
        // Goals fetch failure should NOT clear calendar data
      }

      // Set loading false at the end
      if (isMounted) {
        setLoading(false);
      }
    };

    fetchHealthData();

    // Cleanup: abort fetch and prevent state updates
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [currentMonth, currentYear, clientId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  const handleMouseEnter = (event: React.MouseEvent, day: HealthDay) => {
    if (!day.data || !day.isCurrentMonth) return;

    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    const mouseX = event.clientX;
    const mouseY = event.clientY;

    const timeout = setTimeout(() => {
      setTooltip({
        visible: true,
        data: day.data!,
        x: mouseX,
        y: mouseY
      });
    }, 400);

    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const goToPreviousMonth = () => {
    const newMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const newYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    onMonthChange?.(newMonth, newYear);
  };

  const goToNextMonth = () => {
    const newMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const newYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    onMonthChange?.(newMonth, newYear);
  };

  const goToToday = () => {
    // Use timezone-aware date to navigate to the correct month/year
    const todayStr = getToday(); // Returns YYYY-MM-DD in user's timezone
    const [year, month] = todayStr.split('-').map(Number);
    setCurrentMonth(month - 1); // Month is 0-indexed
    setCurrentYear(year);
    onMonthChange?.(month - 1, year);
  };

  const getStatusColor = (status: HealthDay['status']): string => {
    if (status === 'no-data' || !status) return 'transparent';
    return getScoreStatusColor(status);
  };

  const getCellBackgroundColor = (day: HealthDay) => {
    if (!day.isCurrentMonth) {
      return 'var(--bg-secondary)';
    }

    // Today with data = in-progress (handled via CSS class, no score color)
    if (day.isToday && day.data) {
      return 'var(--bg-primary)';
    }

    if (!day.data) {
      return 'var(--bg-primary)';
    }

    const tierStatus = day.data.tierStatus;

    if (tierStatus) {
      if (tierStatus === 'tier3') {
        return day.status !== 'no-data' ? getStatusColor(day.status) : 'var(--bg-primary)';
      }
      if (tierStatus === 'tier2') {
        return '#9C27B0';
      }
      if (tierStatus === 'tier1') {
        return '#673AB7';
      }
      return 'var(--bg-primary)';
    }

    if (day.status !== 'no-data') {
      return getStatusColor(day.status);
    }

    return 'var(--bg-primary)';
  };

  const generateCalendarDays = (): HealthDay[] => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const firstCalendarDay = new Date(firstDay);
    firstCalendarDay.setDate(firstDay.getDate() - firstDay.getDay());

    const days: HealthDay[] = [];

    for (let i = 0; i < 42; i++) {
      const currentDate = new Date(firstCalendarDay);
      currentDate.setDate(firstCalendarDay.getDate() + i);

      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      const dayHealthData = healthData.find(d => d.date === dateString);

      days.push({
        date: currentDate,
        isCurrentMonth: currentDate.getMonth() === currentMonth,
        isToday: isTodayForClient(dateString),  // Use client's timezone for day boundaries
        score: dayHealthData?.score || 0,
        status: dayHealthData?.status || 'no-data',
        data: dayHealthData
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  return (
    <div className="micro-calendar">
      {/* Header - Always visible */}
      <div className="micro-calendar-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="header-left">
          <Calendar size={14} />
          <h4>Calendar</h4>
          {isCollapsed && (
            <span className="collapsed-month">{MONTHS[currentMonth]} {currentYear}</span>
          )}
        </div>
        <div className="header-buttons">
          {!isCollapsed && onExpand && (
            <button
              className="expand-btn"
              title="Expand calendar view"
              onClick={(e) => { e.stopPropagation(); onExpand(currentMonth, currentYear); }}
            >
              <Maximize2 size={14} />
            </button>
          )}
          <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <>
          {/* Month Navigation */}
          <div className="month-nav-row">
            <button onClick={goToPreviousMonth} className="nav-btn" title="Previous month">
              <ChevronLeft size={14} />
            </button>
            <button onClick={goToToday} className="month-label" title="Go to today">
              {MONTHS[currentMonth]} {currentYear}
            </button>
            <button onClick={goToNextMonth} className="nav-btn" title="Next month">
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              Loading...
            </div>
          )}

          {/* Calendar Container - Black background to match legacy */}
          <div className="micro-calendar-body">
            {/* Calendar Grid */}
            {!loading && (
              <div className="micro-calendar-grid">
                {/* Weekday Headers */}
                {WEEKDAYS.map((day, index) => (
                  <div key={`weekday-${index}`} className="weekday-header">
                    {day}
                  </div>
                ))}

                {/* Calendar Days */}
                {calendarDays.map((day, index) => {
                  const cellBackgroundColor = getCellBackgroundColor(day);
                  const tierStatus = day.data?.tierStatus;

                  const isInProgress = day.isToday && !!day.data;
                  const shouldUseWhiteText = !isInProgress && (
                    tierStatus === 'tier2' ||
                    tierStatus === 'tier1' ||
                    (tierStatus === 'tier3' && day.status !== 'no-data')
                  );

                  return (
                    <div
                      key={index}
                      className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''} ${day.data ? 'has-data' : ''} ${isInProgress ? 'in-progress' : ''}`}
                      style={{
                        backgroundColor: !day.isCurrentMonth ? 'var(--bg-secondary)' : cellBackgroundColor,
                        color: day.isCurrentMonth && shouldUseWhiteText ? 'white' : 'var(--text-primary)',
                      }}
                      onMouseEnter={(e) => handleMouseEnter(e, day)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {day.date.getDate()}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Mini Legend */}
            <div className="micro-legend">
              <div className="legend-item">
                <div className="legend-dot" style={{ backgroundColor: SCORE_COLORS.excellent }} />
                <span>Excellent</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot" style={{ backgroundColor: SCORE_COLORS.good }} />
                <span>Good</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot" style={{ backgroundColor: SCORE_COLORS.fair }} />
                <span>Fair</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot" style={{ backgroundColor: SCORE_COLORS.atRisk }} />
                <span>At Risk</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot" style={{ backgroundColor: SCORE_COLORS.needsSupport }} />
                <span>Needs Support</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Tooltip */}
      {tooltip.data && (
        <CalendarDayTooltip
          data={tooltip.data}
          visible={tooltip.visible}
          x={tooltip.x}
          y={tooltip.y}
          showAllMetrics={showAllMetrics}
          hideMatchingSoftScore={hideMatchingSoftScore}
          userGoals={userGoals}
          isCoachView={true}
        />
      )}

      <style>{`
        .micro-calendar {
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-large);
          overflow: hidden;
          height: fit-content;
          flex-shrink: 0;
        }

        .micro-calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .micro-calendar-header:hover {
          background: var(--bg-tertiary);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .header-left svg {
          color: var(--text-secondary);
        }

        .micro-calendar-header h4 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .collapsed-month {
          font-size: 12px;
          color: var(--text-secondary);
          font-weight: 400;
        }

        .header-buttons {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .expand-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .expand-btn:hover {
          background: var(--bg-tertiary);
          color: var(--accent-green);
        }

        .collapse-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .collapse-btn:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        .month-nav-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 0 20px 12px;
        }

        .nav-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: none;
          border: none;
          border-radius: 4px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s;
        }

        .nav-btn:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        .month-label {
          background: none;
          border: none;
          padding: 4px 8px;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-primary);
          cursor: pointer;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .month-label:hover {
          background: var(--bg-tertiary);
        }

        .loading-state {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 120px;
          color: var(--text-secondary);
          font-size: 12px;
        }

        .micro-calendar-body {
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          margin: 0 16px 16px;
          padding: 12px;
        }

        .micro-calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 1px;
          background: var(--border-secondary);
          border-radius: 4px;
          overflow: hidden;
        }

        .weekday-header {
          background: var(--bg-secondary);
          padding: 3px;
          font-size: 9px;
          font-weight: 500;
          color: var(--text-secondary);
          text-align: center;
        }

        .calendar-day {
          min-height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 400;
          background: var(--bg-primary);
          cursor: default;
          position: relative;
        }

        .calendar-day.other-month {
          opacity: 0.4;
        }

        .calendar-day.today {
          font-weight: 600;
        }

        .calendar-day.in-progress {
          background: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 4px,
            rgba(59, 130, 246, 0.4) 4px,
            rgba(59, 130, 246, 0.4) 8px
          ) !important;
          background-attachment: fixed !important;
        }

        .calendar-day.has-data {
          cursor: pointer;
        }

        .micro-legend {
          display: flex;
          justify-content: center;
          gap: 12px;
          padding: 10px 0 0;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .legend-dot {
          width: 6px;
          height: 6px;
          border-radius: 1px;
        }

        .legend-item span {
          font-size: 9px;
          color: var(--text-secondary);
        }
      `}</style>
    </div>
  );
};
